import * as React from "react";
import { Handle, NodeToolbar, Position, useStore, type NodeProps } from "@xyflow/react";

import { SIDEBAR_BRAND_LOGO_SRC } from "@/constants/sidebarBrandLogo";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDisplayCurrency } from "@/contexts/DisplayCurrencyContext";
import { useTradeStatistics } from "@/hooks/useTradeStatistics";
import type { TradeStatisticsFilters } from "@/types/trade";
import {
  WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "../workspaceGraphConstants";
import {
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG,
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
  WORKSPACE_EXECUTING_ACCOUNT_TOOLBAR_ANCHOR_INSETS,
  workspaceExecutingAccountToolbarScreenOffset,
  type WorkspaceExecutingAccountNodeData,
} from "../workspaceExecutingAccountUtils";
import {
  useWorkspaceNodeToolbarSide,
  WORKSPACE_NODE_TOOLBAR_OFFSET_PX,
} from "../useWorkspaceNodeToolbarSide";
import { WorkspaceExecutingAccountActionsMenu } from "../WorkspaceNodeActions";
import {
  cnSimulatorMetricsDelta,
  SIMULATOR_METRICS_GRAY,
} from "../../ExecutionRulesSimulator/simulatorMetricsTypography";

const EDGE_HANDLE_CLASS =
  "!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent !opacity-0";

import {
  EXECUTING_ACCOUNT_SUMMARY_PRESETS,
  formatExecutingAccountWinRatePercent,
  type ExecutingAccountStatsTimeframe,
} from "../executingAccountSummaryPresets";

const TYPE_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-[9px] font-medium uppercase tracking-wide text-[color:var(--brand-tropical-surf)]";

const CARD_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-[13px] font-medium leading-5 text-[color:var(--brand-tropical-surf)] dark:text-white";

const META_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-[9px] font-medium uppercase tracking-wide text-muted-foreground";

function formatWinRatePercent(winRate: number | null | undefined): string {
  return formatExecutingAccountWinRatePercent(winRate);
}

function ExecutingAccountCardBody({
  accountCode,
  brokerName,
  label,
}: {
  accountCode: string;
  brokerName?: string | null;
  label: string;
}): React.ReactElement {
  const { formatMoney } = useDisplayCurrency();
  const [preset, setPreset] = React.useState<ExecutingAccountStatsTimeframe>("30-days");
  const filters = React.useMemo((): TradeStatisticsFilters | undefined => {
    const code = accountCode.trim();
    if (!code) return undefined;
    const broker = brokerName?.trim().toLowerCase();
    return {
      broker_account_code: code,
      ...(broker ? { broker_name: broker } : {}),
      timeframe: preset,
      fields: "basic",
    };
  }, [accountCode, brokerName, preset]);
  const { statistics, loading } = useTradeStatistics(filters, {
    enabled: Boolean(filters),
  });

  const pnl = statistics?.total_pnl;
  const winRate = statistics?.win_rate;
  const tradeCount = statistics?.closed_trades ?? null;
  const pnlTone =
    typeof pnl === "number" && Number.isFinite(pnl) ? pnl : null;

  return (
    <div
      data-workspace-executing-account-metrics=""
      className="flex h-full min-h-0 flex-col justify-between px-3 py-2.5"
      style={{ paddingLeft: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG + 12 }}
    >
      <div className="min-w-0">
        <p data-workspace-node-type-label="" className={cn(TYPE_LABEL_CLASS, "m-0")}>
          Executing account
        </p>
        <p
          data-workspace-node-title=""
          className={cn(CARD_TITLE_CLASS, "m-0 mt-0.5 truncate whitespace-nowrap")}
          title={label}
        >
          {label || "Account"}
        </p>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>PnL</p>
          <p
            className={cn(
              "truncate font-[family-name:var(--font-primary)] text-[12px] font-medium tabular-nums leading-snug",
              loading
                ? SIMULATOR_METRICS_GRAY
                : cnSimulatorMetricsDelta(pnlTone, "")
            )}
          >
            {loading
              ? "…"
              : typeof pnl === "number" && Number.isFinite(pnl)
                ? formatMoney(pnl, { showSign: true })
                : "—"}
          </p>
        </div>
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>Win rate</p>
          <p
            className={cn(
              "truncate font-[family-name:var(--font-primary)] text-[12px] font-medium tabular-nums leading-snug",
              SIMULATOR_METRICS_GRAY
            )}
          >
            {loading ? "…" : formatWinRatePercent(winRate)}
          </p>
        </div>
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>Trades</p>
          <p
            className={cn(
              "truncate font-[family-name:var(--font-primary)] text-[12px] font-medium tabular-nums leading-snug",
              SIMULATOR_METRICS_GRAY
            )}
          >
            {loading
              ? "…"
              : typeof tradeCount === "number" && Number.isFinite(tradeCount)
                ? tradeCount.toLocaleString()
                : "—"}
          </p>
        </div>
      </div>

      <div
        className="nodrag nopan nowheel mt-2 min-w-0"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <Select
          value={preset}
          onValueChange={(next) => {
            setPreset(next as ExecutingAccountStatsTimeframe);
          }}
        >
          <SelectTrigger
            size="sm"
            aria-label="Date range preset"
            className={cn(
              "nodrag nopan nowheel w-full",
              "text-gray-500 dark:text-white",
              "[&_[data-slot=select-value]]:!text-gray-500 dark:[&_[data-slot=select-value]]:!text-white",
              "[&_svg]:!text-gray-500 dark:[&_svg]:!text-white"
            )}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent
            className="nodrag nopan nowheel"
            onPointerDown={(event) => event.stopPropagation()}
          >
            {EXECUTING_ACCOUNT_SUMMARY_PRESETS.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/**
 * Pipeline entity card with the account diamond half-in / half-out on the left.
 */
export function WorkspaceExecutingAccountNode({
  data,
  selected,
}: NodeProps): React.ReactElement {
  const flowData = data as WorkspaceExecutingAccountNodeData;
  const label = flowData.label?.trim() || "";
  const isSelected = Boolean(selected || flowData.isSelected || flowData.isParentSelected);
  const focusFilterDimmed = Boolean(flowData.focusFilterDimmed);
  const showActionsMenu = Boolean(flowData.showActionsMenu);
  const toolbarSide = useWorkspaceNodeToolbarSide(
    showActionsMenu,
    undefined,
    undefined,
    WORKSPACE_EXECUTING_ACCOUNT_TOOLBAR_ANCHOR_INSETS
  );
  const zoom = useStore((state) => state.transform[2]);
  const toolbarScreenOffset = showActionsMenu
    ? workspaceExecutingAccountToolbarScreenOffset(
        toolbarSide.position,
        toolbarSide.align,
        zoom
      )
    : { x: 0, y: 0 };
  const accountCode = flowData.account?.brokerAccountCode?.trim() || "";
  const brokerName = flowData.account?.brokerName ?? null;

  return (
    <>
      <div
        data-workspace-node=""
        data-workspace-node-type="executing-account"
        data-workspace-node-variant="entity-card"
        data-workspace-node-focus-dimmed={focusFilterDimmed ? "true" : undefined}
        className={cn("group relative", focusFilterDimmed && "opacity-40")}
        style={{
          width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
          height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
        }}
      >
        {/* Standard pipeline card; left edge sits on the diamond center. */}
        <div
          data-workspace-node-shell="executing-account"
          className={cn(
            "absolute top-0 h-full overflow-hidden rounded-lg border border-solid bg-card text-card-foreground shadow-sm transition-[box-shadow,border-color] dark:bg-sidebar dark:text-sidebar-foreground",
            isSelected && "ring-2 ring-primary/45 ring-offset-1 ring-offset-background"
          )}
          style={{
            left: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG,
            width: WORKSPACE_NODE_CARD_WIDTH,
            height: WORKSPACE_NODE_CARD_HEIGHT,
            borderWidth: 1.5,
            borderStyle: "solid",
            borderColor: "#6b7280",
          }}
        >
          <ExecutingAccountCardBody
            accountCode={accountCode}
            brokerName={brokerName}
            label={label}
          />
        </div>

        {/* Account diamond: half in the card, half out to the left. */}
        <div
          className="pointer-events-none absolute top-1/2 z-[1] flex -translate-y-1/2 items-center justify-center"
          style={{
            left: 0,
            width: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
            height: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
          }}
        >
          <div
            className={cn(
              "flex rotate-45 items-center justify-center overflow-hidden rounded-lg border border-solid bg-card text-card-foreground shadow-sm dark:bg-sidebar dark:text-sidebar-foreground",
              isSelected && "ring-2 ring-primary/45 ring-offset-1 ring-offset-background"
            )}
            style={{
              width: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
              height: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
              borderWidth: 1.5,
              borderStyle: "solid",
              borderColor: "#6b7280",
            }}
          >
            <img
              src={SIDEBAR_BRAND_LOGO_SRC}
              alt={label || "Executing account"}
              className="size-12 -rotate-45 object-contain"
              draggable={false}
            />
          </div>
        </div>

        <Handle
          id="target"
          type="target"
          position={Position.Left}
          className={EDGE_HANDLE_CLASS}
          style={{ left: 0, top: "50%", transform: "translate(-50%, -50%)" }}
          isConnectable={false}
        />
        <Handle
          id="source"
          type="source"
          position={Position.Right}
          className={EDGE_HANDLE_CLASS}
          style={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }}
          isConnectable={false}
        />
      </div>
      <NodeToolbar
        isVisible={showActionsMenu}
        position={toolbarSide.position}
        align={toolbarSide.align}
        offset={WORKSPACE_NODE_TOOLBAR_OFFSET_PX}
        className="nodrag nopan nowheel"
      >
        <div
          style={
            toolbarScreenOffset.x || toolbarScreenOffset.y
              ? {
                  transform: `translate(${toolbarScreenOffset.x}px, ${toolbarScreenOffset.y}px)`,
                }
              : undefined
          }
        >
          <WorkspaceExecutingAccountActionsMenu
            label={label}
            account={flowData.account}
            mutating={flowData.mutating}
            onOpenDetail={flowData.onOpenDetail}
            onOpenAccountWorkspace={flowData.onOpenAccountWorkspace}
            onRemove={flowData.onRemove}
            onClose={flowData.onCloseActionsMenu}
          />
        </div>
      </NodeToolbar>
    </>
  );
}
